/**
 * CotacaoList - Lista de cotações da aba "Cotações" (extraído de Compras.jsx)
 * Componente de apresentação: recebe dados e handlers via props.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plus, Edit, Trash2, FileText, Send, MessageCircle,
  Eye, ShoppingCart, Building2, Package, Calendar, Users, Download, Trophy,
} from 'lucide-react';
import { StatusBadge, StatusLegend } from './utils';
import { getCurrencyMeta } from '../../lib/currencies';

const CotacaoList = ({
  cotacoes,
  isAdmin,
  onNovaCotacao,
  onViewCotacao,
  onEditCotacao,
  onEnviarCotacao,
  onOpenRespostaDialog,
  onOpenMesaDecisao,
  onGerarPedido,
  onDeleteCotacao,
  onPreviewPdf,
  onDownloadPdf,
}) => {
  return (
    <>
      {/* Info sobre código */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
        <strong>Cotações (COT):</strong> Solicitações de preço enviadas aos fornecedores. O código COT-ANO-XXXX é o número único da cotação. Após receber as respostas, você pode comparar e aprovar o melhor fornecedor, gerando automaticamente um Pedido de Compra.
      </div>

      {/* Legenda de status (recolhível) */}
      <StatusLegend grupo="cotacao" />

      {cotacoes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Nenhuma cotação encontrada</h3>
            <p className="text-gray-500 mb-4">Crie sua primeira cotação para começar</p>
            <Button onClick={onNovaCotacao}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Cotação
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {cotacoes.map(cotacao => (
            <Card
              key={cotacao.id}
              className={`hover:shadow-md transition-shadow ${cotacao.urgente ? 'border-red-500 border-2 bg-red-50 shadow-red-100 shadow-md ring-2 ring-red-200' : ''}`}
              data-testid={`cotacao-card-${cotacao.numero}`}
            >
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Info Principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`font-mono text-sm font-medium ${cotacao.urgente ? 'text-red-600' : 'text-blue-600'}`}>{cotacao.numero}</span>
                      <StatusBadge status={cotacao.status} />
                      {cotacao.urgente && (
                        <Badge className="bg-red-600 text-white hover:bg-red-600 animate-pulse font-bold px-3">
                          🚨 URGENTE
                        </Badge>
                      )}
                      {cotacao.origem === 'ficha_tecnica' && (
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          Ficha Técnica
                        </Badge>
                      )}
                      {cotacao.projeto_nome && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid={`cotacao-projeto-tag-${cotacao.numero}`}>
                          {cotacao.projeto_nome}
                        </Badge>
                      )}
                      {(cotacao.moeda && cotacao.moeda !== 'BRL') && (
                        <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-700" data-testid={`cotacao-moeda-${cotacao.numero}`}>
                          {getCurrencyMeta(cotacao.moeda).symbol} {cotacao.moeda}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900 truncate">{cotacao.titulo}</h3>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {cotacao.empresa_razao_social?.split(' ').slice(0, 2).join(' ') || 'Empresa'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        {cotacao.itens?.length || 0} itens
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {cotacao.fornecedores?.length || 0} fornecedores
                      </span>
                      {cotacao.data_limite_resposta && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Até {new Date(cotacao.data_limite_resposta).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewCotacao(cotacao)}
                      data-testid={`btn-view-${cotacao.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>

                    {/* Botão Editar - disponível em rascunho, aguardando, analisando e aprovada_parcial */}
                    {['rascunho', 'aguardando_respostas', 'analisando', 'aprovada_parcial'].includes(cotacao.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditCotacao(cotacao)}
                        data-testid={`btn-edit-${cotacao.id}`}
                        title={['analisando', 'aprovada_parcial'].includes(cotacao.status) ? 'Editar (cotação com respostas — confirmação necessária)' : 'Editar cotação'}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}

                    {cotacao.status === 'rascunho' && (
                      <Button
                        size="sm"
                        onClick={() => onEnviarCotacao(cotacao)}
                        data-testid={`btn-enviar-${cotacao.id}`}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Enviar
                      </Button>
                    )}

                    {/* Botão Registrar Resposta - sempre disponível em status editável.
                        Se todos já responderam, abre em modo "atualizar resposta" — útil quando
                        novos itens são adicionados após a primeira resposta (Bug 3). */}
                    {['aguardando_respostas', 'analisando', 'aprovada_parcial'].includes(cotacao.status) && (cotacao.fornecedores?.length > 0) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenRespostaDialog(cotacao)}
                        data-testid={`btn-resposta-${cotacao.id}`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {cotacao.fornecedores?.some(f => f.status !== 'respondido')
                          ? 'Registrar Resposta'
                          : 'Atualizar Resposta'}
                        {cotacao.fornecedores?.filter(f => f.status !== 'respondido').length > 0 && (
                          <Badge className="ml-2 bg-orange-500 text-white text-xs">
                            {cotacao.fornecedores.filter(f => f.status !== 'respondido').length} pendente(s)
                          </Badge>
                        )}
                      </Button>
                    )}

                    {(cotacao.status === 'analisando' || cotacao.status === 'aprovada_parcial') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenMesaDecisao(cotacao)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`btn-mesa-decisao-${cotacao.id}`}
                        title="Comparar propostas dos fornecedores e escolher vencedor"
                      >
                        <Trophy className="w-4 h-4 mr-1" />
                        Comparar
                      </Button>
                    )}

                    {cotacao.status === 'aprovada' && !cotacao.pedido_gerado_id && (
                      <Button
                        size="sm"
                        onClick={() => onGerarPedido(cotacao)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`btn-gerar-pedido-${cotacao.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Gerar Pedido
                      </Button>
                    )}

                    {isAdmin && cotacao.status !== 'excluida' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteCotacao(cotacao.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-delete-${cotacao.id}`}
                        title="Excluir cotação"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}

                    {/* Botões PDF para cotação */}
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPreviewPdf('cotacao', cotacao.id, cotacao.numero)}
                        title="Visualizar PDF"
                        data-testid={`btn-preview-cotacao-${cotacao.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDownloadPdf(cotacao.id, cotacao.numero)}
                        title="Baixar PDF"
                        data-testid={`btn-pdf-cotacao-${cotacao.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default CotacaoList;
